import React, { memo } from 'react';
import AddressTagFilter from 'application/components/AddressTagFilter';
import { InputSize } from '@zvuk-b2b/react-uikit/ui/InputSearch';

import { ADDRESS_REPORT_TAG_FILTER_TEST_IDS, DATA_TEST_IDS } from '../consts';
import { ADDRESS_REPORT_TAG_FILTER_TEXTS, TEXTS } from '../texts';
import AddressOptionsList from './AddressOptionsList';
import AddressSelectTrigger from './AddressSelectTrigger';
import AddressSelectionActions from './AddressSelectionActions';
import type { AddressReportPointsSelectProps } from './types';
import { useAddressReportPointsSelectViewModel } from './useAddressReportPointsSelectViewModel';

import styles from './index.module.scss';

const AddressReportPointsSelect: React.FC<AddressReportPointsSelectProps> = ({
  addresses,
  isLoading,
  selectedAddressIds,
  tags,
  onChange,
}) => {
  const {
    containerRef,
    dropdownId,
    dropdownState,
    filteredAddresses,
    hasSelectedAddresses,
    hasUnselectedAddresses,
    isDropdownOpen,
    isExactSearch,
    isLoading: isOptionsLoading,
    isTagFilterOpen,
    search,
    selectedAddressIdSet,
    selectedCount,
    totalCount,
    selectedTagIds,
    onAddressChange,
    onDropdownToggle,
    onExactSearchChange,
    onReset,
    onSearchChange,
    onSelectAll,
    onOpenChange,
    onSelectedTagIdsChange,
  } = useAddressReportPointsSelectViewModel({
    addresses,
    isLoading,
    selectedAddressIds,
    tags,
    onChange,
  });

  return (
    <div
      className={styles.container}
      data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES}
      ref={containerRef}>
      <span
        id={DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_LABEL}
        className={styles.label}
        data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_LABEL}>
        {TEXTS.AUDIO_REPORT_ADDRESSES_LABEL}
      </span>

      <AddressSelectTrigger
        dropdownId={dropdownId}
        isOpen={isDropdownOpen}
        selectedCount={selectedCount}
        totalCount={totalCount}
        onClick={onDropdownToggle}
      />

      {isDropdownOpen && (
        <div
          id={dropdownId}
          className={styles.dropdown}
          style={{
            position: 'fixed',
            maxHeight: dropdownState.maxHeight,
            top: dropdownState.fixedPosition?.top,
            left: dropdownState.fixedPosition?.left,
            width: dropdownState.fixedPosition?.width,
          }}
          data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_DROPDOWN}>
          <div className={styles.searchArea}>
            <AddressTagFilter
              tags={tags}
              selectedTagIds={selectedTagIds}
              isExactSearch={isExactSearch}
              isOpen={isTagFilterOpen}
              search={search}
              isLoading={isOptionsLoading}
              searchSize={InputSize.SMALL}
              texts={ADDRESS_REPORT_TAG_FILTER_TEXTS}
              testIds={ADDRESS_REPORT_TAG_FILTER_TEST_IDS}
              onExactSearchChange={onExactSearchChange}
              onOpenChange={onOpenChange}
              onSearchChange={onSearchChange}
              onSelectedTagIdsChange={onSelectedTagIdsChange}
            />
          </div>

          <div
            className={styles.divider}
            data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_DIVIDER}
          />

          <AddressSelectionActions
            hasSelectedAddresses={hasSelectedAddresses}
            hasUnselectedAddresses={hasUnselectedAddresses}
            selectedCount={selectedCount}
            onReset={onReset}
            onSelectAll={onSelectAll}
          />

          <AddressOptionsList
            addresses={filteredAddresses}
            isLoading={isOptionsLoading}
            selectedAddressIdSet={selectedAddressIdSet}
            onAddressChange={onAddressChange}
          />
        </div>
      )}
    </div>
  );
};

export default memo(AddressReportPointsSelect);
